import { z } from 'zod';
import { Id, IsoDateTime } from './common.js';
import { ChannelType, MessageType } from './channels.js';

// ─── Fiwano inbound webhook payloads ─────────────────────────────────────────

export const FiwanoMediaPayload = z.object({
  media_id: z.string(),
  kind: z.string(),
  mime_type: z.string(),
  file_size: z.number().int().nullable(),
  filename: z.string().nullable(),
  sha256: z.string().nullable().optional(),
  duration_ms: z.number().int().nullable().optional(),
  download_url: z.string().nullable(),
  expires_at: z.string().nullable().optional(),
  error: z.string().optional(),
});

export const FiwanoEventType = z.enum([
  'message.received',
  'message.sent',
  'message.delivered',
  'message.read',
  'message.failed',
]);

export const FiwanoMessageReceivedData = z.object({
  message_id: z.string(),
  from: z.string(),
  from_name: z.string().nullable(),
  type: MessageType,
  text: z.string().nullable().optional(),
  caption: z.string().nullable().optional(),
  media: FiwanoMediaPayload.nullable().optional(),
  unsupported_type: z.string().optional(),
  upgrade_required: z.string().optional(),
});

export const FiwanoStatusData = z.object({
  message_id: z.string(),
  status: z.enum(['sent', 'delivered', 'read', 'failed']),
  recipient: z.string(),
  error: z.string().optional(),
  errors: z.array(z.object({ code: z.number(), title: z.string() })).optional(),
});

export const FiwanoWebhookEnvelope = z.object({
  event: FiwanoEventType,
  channel_id: z.string(),
  channel_type: ChannelType,
  timestamp: IsoDateTime,
  data: z.union([FiwanoMessageReceivedData, FiwanoStatusData]),
});
export type FiwanoWebhookEnvelope = z.infer<typeof FiwanoWebhookEnvelope>;

// ─── Platform integration webhooks (outbound by us, inbound from platforms) ─

export const PlatformEventType = z.enum([
  'product.created',
  'product.updated',
  'product.deleted',
  'inventory.changed',
  'order.created',
  'order.status_changed',
  'order.cancelled',
  'customer.created',
]);
export type PlatformEventType = z.infer<typeof PlatformEventType>;

export const PlatformWebhookEnvelope = z.object({
  event: PlatformEventType,
  platform_id: Id,
  merchant_external_id: z.string(),
  timestamp: IsoDateTime,
  idempotency_key: z.string(),
  data: z.record(z.unknown()),
});
